import type { PostHog } from 'posthog-js/react';

/**
 * Accesses the analytics instance from window.analytics (loaded via HTML
 * script tag). Non-React: safe to call from xstate files and other
 * non-component code, as well as from React via `useAnalytics()` in
 * `hooks/use-analytics.ts`.
 *
 * @returns Analytics instance or null if not available
 */
export function getAnalytics(): PostHog | null {
  // Always use window.analytics (loaded from HTML script tag)
  if (typeof window !== 'undefined' && window.analytics) {
    return window.analytics;
  }

  return null;
}

/**
 * The event names of the PSTUDIO analytics rollout — a roadmap, not an
 * inventory of what this revision emits. Only `solution_published` and
 * `solution_published_failed` have a call site here; the four CSV and download
 * names are declared up front so the follow-up changes in the same stack add a
 * call site without widening the union again.
 *
 * `channel_created` and `channel_updated` are absent on purpose: they are
 * captured inline in `prostudio.xstate.ts`, predate this helper, and are
 * deliberately left as-is rather than migrated, to avoid touching
 * already-shipped, live-production call sites.
 */
export type ProStudioAnalyticsEvent =
  | 'solution_published'
  | 'solution_published_failed'
  | 'csv_uploaded'
  | 'csv_upload_failed'
  | 'solution_downloaded'
  | 'solution_download_failed';

/**
 * The support error ID `throwErrorWithId` appends to every message it raises:
 * `(Error ID: <uuid4> please pass this ID to the support service)`.
 * @see packages/utils/errors/generate-error-id.ts
 */
const SUPPORT_ERROR_ID_SUFFIX =
  /\s*\(Error ID: ([0-9a-f-]{36}) please pass this ID to the support service\)\s*$/i;

/**
 * Splits the support error ID out of a message raised by `throwErrorWithId`.
 *
 * Every failure path in the pro-studio invokes goes through that helper, so
 * without this split every `*_failed` event carries a unique `error_message`
 * and a "top reasons X fails" breakdown is a list of rows with count 1. The id
 * is still worth sending — it is what joins the event to its Sentry report —
 * but as its own property, not inside the text the dashboard groups on.
 *
 * A message raised by anything else comes back unchanged with a `null` id.
 */
export function splitSupportErrorId(message: string): {
  message: string;
  errorId: string | null;
} {
  const match = message.match(SUPPORT_ERROR_ID_SUFFIX);

  if (!match) {
    return { message, errorId: null };
  }

  return { message: message.slice(0, match.index), errorId: match[1] };
}

/**
 * Captures a PostHog event if analytics is available and the user has opted
 * in. Use this for all new event instrumentation instead of calling
 * `getAnalytics().capture(...)` directly.
 *
 * Analytics is best-effort and must never decide a user-facing outcome. This
 * function is SYNCHRONOUS and returns `void`; what keeps a failing capture
 * from interrupting its caller is the `try/catch` around its whole body, not
 * anything the caller does with the return value.
 *
 * The guard it provides stops at the function boundary: an expression
 * evaluated in the ARGUMENT list (a `.slice` on a value the caller believes is
 * a string, say) runs before this body and is not covered. Build the payload
 * from values that cannot throw, or build it inside the caller's own guard.
 */
export function captureAnalyticsEvent(
  name: ProStudioAnalyticsEvent,
  properties?: Record<string, unknown>
): void {
  try {
    const analytics = getAnalytics();

    if (
      analytics &&
      typeof analytics.has_opted_in_capturing === 'function' &&
      analytics.has_opted_in_capturing() &&
      typeof analytics.capture === 'function'
    ) {
      analytics.capture(name, properties);
    }
  } catch {
    // Best-effort: see the function doc above.
  }
}

/**
 * The identity block every event captured through `captureAnalyticsEvent`
 * carries. NOT every Pro Studio analytics event: `channel_created` and
 * `channel_updated` are captured inline in `prostudio.xstate.ts` and send a
 * different set (`user_email`, `channel_id`, `pype_id`, …) with none of
 * `customer_id`, `project_name` or `solution_name`.
 */
export type SolutionAnalyticsProps = {
  customer_id: string | undefined;
  project_id: string | undefined;
  project_name: string | undefined;
  solution_name: string | undefined;
};

/**
 * Builds the shared `customer_id`/`project_id`/`project_name`/`solution_name`
 * block from context. Structurally typed (not `ProStudioContext`) so this
 * module — imported from both XState invokes and React hooks — never has to
 * import the xstate types module back.
 *
 * `customer_id` is deliberately the STABLE id under
 * `project.projectProStudioSetting.customerId`, not `ctx.customerId` — that
 * field is actually the customer's display NAME (see get-project-data.ts),
 * and would break historical joins if the customer were renamed.
 */
export function solutionAnalyticsProps(ctx: {
  project?: {
    id?: string;
    name?: string;
    projectProStudioSetting?: { customerId?: string | null } | null;
  } | null;
  selectedBot?: { displayName?: string } | null;
}): SolutionAnalyticsProps {
  return {
    customer_id: ctx.project?.projectProStudioSetting?.customerId ?? undefined,
    project_id: ctx.project?.id,
    project_name: ctx.project?.name,
    solution_name: ctx.selectedBot?.displayName,
  };
}

// PSTUDIO-4194: a backend error message can carry row-level content
// (`checkForErrors` prefers it over a generic message). What this bound does
// and does not do, stated rather than implied: it caps the SIZE of what leaves
// the browser, and nothing else. It does not sanitize — the front of the
// message, which is where a constraint violation puts the offending value, is
// exactly the part that survives. Sentry adds no second bound either:
// `maxValueLength` is set nowhere in `packages/utils/sentry-init/`, and in
// `@sentry/core` both truncations sit behind `if (maxValueLength)` with no
// default. `error_kind` and the per-event stage property are the bounded axes
// a dashboard groups on; `error_message` is for a human reading one event.
// Shared across failure events so they stay comparable.
export const MAX_ANALYTICS_ERROR_MESSAGE_LENGTH = 250;

/** The bounded, safe-to-dashboard axis `error_message` cannot be. */
export type AnalyticsErrorKind =
  | 'constraint_violation'
  | 'auth'
  | 'network'
  | 'validation'
  | 'unknown';

/**
 * Classifies a backend error message into a fixed, groupable set.
 *
 * Call it on the string that is actually SENT — i.e. after
 * `.slice(0, MAX_ANALYTICS_ERROR_MESSAGE_LENGTH)` — so `error_kind` and
 * `error_message` on one event can never describe different text.
 */
export function classifyAnalyticsError(message: string): AnalyticsErrorKind {
  // `violates <name> constraint` covers the check/foreign-key/not-null forms
  // Postgres words differently from the unique one.
  if (
    /unique constraint|duplicate key|violates [\w -]*constraint/i.test(message)
  ) {
    return 'constraint_violation';
  }
  // `access-denied` / `not accessible for role` is how a lost session reaches
  // the `admin_` operations: the auth webhook returns a 401 for the hardcoded
  // list and Hasura surfaces it in that wording, not as `401`.
  // @see packages/api-services/session/auth-error.test.ts
  if (
    /authentication|unauthorized|permission|access-denied|not accessible for role/i.test(
      message
    )
  ) {
    return 'auth';
  }
  if (/timeout|timed out|network/i.test(message)) {
    return 'network';
  }
  if (/validation|invalid/i.test(message)) {
    return 'validation';
  }
  return 'unknown';
}
